<template>
  <div
    ref="inputWrapper"
    class="px-2 py-4.5 w-full flex items-center rounded-lg cursor-text relative border border-transparent z-10 transition-all duration-200 min-h-14"
    :class="{'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    style="box-shadow: 0 5px 10px rgba(31, 31, 31, 0.12)"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
    @click="!focused ? focus() : ''"
  >
    <span
      ref="label"
      class="top-4.5 text-16 font-normal absolute z-20 leading-5 text-gray transition-all duration-200 pointer-events-none"
      :class="{[labelClass]: true, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    >
      {{ label }}
    </span>

    <quill-editor
      ref="input"
      :options="editorOption"
      @click.native.stop
      style="transform: translate(0)"
      @blur="blur()"
      @change="input()"
      @focus="focus()"
      v-model="inputVal"
      class="input w-full mt-5 leading-5 text-black duration-200 opacity-0"
      :class="{'text-gray': disabled, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
      :style="disabled ? {pointerEvents: 'none'} : {}"
    />
    <div ref="rightSide" class="right-2 bottom-4 absolute cursor-pointer">
      <slot name="right-side"></slot>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="absolute left-0 ml-2"
      :color="'red'"
      :style="{bottom: `-${errHeight}px`}"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  import '@/assets/static/scss/quill/quill.bubble.scss'
  import '@/assets/static/scss/quill/quill.core.scss'
  import '@/assets/static/scss/quill/quill.snow.scss'
  import {quillEditor} from '@/integrations/editor'
  import {mapGetters} from 'vuex'
  export default {
    name: 'quill-input',
    props: {
      label: {
        type: String,
        required: true
      },
      labelClass: String,
      inputColor: {
        type: String,
        required: false,
        default: 'rgb(255, 255, 255)'
      },
      value: Object,
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      disabled: Boolean,
      preventAutoFocus: {
        type: Boolean,
        default: true
      }
    },
    components: {
      quillEditor
    },
    data() {
      return {
        inputVal: '',
        interval: null,
        fieldError: '',
        errHeight: 0,
        focused: false,
        isBluring: false,
        isValid: true,
        quillHeight: 0,
        editorOption: {
          placeholder: this.placeholder
        }
      }
    },
    async mounted() {
      await this.componentLoaded()
      this.setContent(this.value)
      this.$nextTick(() => {
        this.quillHeight = this.$refs.input.$el.clientHeight
        this.$refs.input.$el.classList.add('hidden')
        if (this.value !== null && this.value !== '') this.init()
      })
    },
    methods: {
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.input.$el && this.$refs.inputWrapper && this.$refs.label && this.$refs.rightSide && this.$refs.error) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      },
      input() {
        this.$emit('input', this.getValue())
      },
      init() {
        if (!this.focused && this.label) {
          this.focused = true
          this.$refs.input.$el.style.height = '0px'
          this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['p-2'])
          this.$refs.label.classList.replace(
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
          )
          this.$refs.input.$el.classList.replace('hidden', 'flex')
          setTimeout(() => {
            this.$refs.input.$el.classList.replace('opacity-0', 'opacity-100')
            this.$refs.input.$el.style.height = this.quillHeight + 'px'
            setTimeout(() => {
              this.$refs.input.$el.style.height = ''
            }, 200)
          }, 0)
        }

        this.$emit('focus')
      },
      focus() {
        if (!this.focused && this.label && !this.isBluring) {
          this.focused = true
          this.$refs.input.$el.style.height = '0px'
          this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['p-2'])
          this.$refs.label.classList.replace(
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
          )
          this.$refs.input.$el.classList.replace('hidden', 'flex')
          setTimeout(() => {
            this.$refs.input.$el.classList.replace('opacity-0', 'opacity-100')
            this.$refs.input.$el.style.height = this.quillHeight + 'px'
            setTimeout(() => {
              this.$refs.input.quill.focus()
              this.$refs.input.$el.style.height = ''
            }, 200)
          }, 0)
        }

        this.$emit('focus')
      },
      blur() {
        if (this.inputVal.length === 0 && this.focused && this.label) {
          this.isBluring = true
          this.focused = false
          this.$refs.input.$el.style.height = this.$refs.input.$el.clientHeight + 'px'
          this.quillHeight = this.$refs.input.$el.clientHeight
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['p-2'], ['px-2', 'py-4.5'])
            this.$refs.label.classList.replace(
              ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold'],
              ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal']
            )
            this.$refs.input.$el.classList.replace('opacity-100', 'opacity-0')
            this.$refs.input.$el.style.height = '0px'
            setTimeout(() => {
              this.$refs.input.$el.classList.replace('flex', 'hidden')
              this.$refs.input.$el.style.height = ''
              this.isBluring = false
            }, 200)
          }, 0)
        }
        this.$emit('blur')
      },
      getValue() {
        return this.inputVal
      },
      getValueRaw() {
        return this.$refs.input.quill.editor.delta
      },
      setContent(val) {
        if (val?.ops) this.$refs.input.quill.setContents([...val.ops])
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    watch: {
      value(newVal) {
        this.setContent(newVal)
      },
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    computed: {
      ...mapGetters(['theme'])
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
